/* eslint-disable react-refresh/only-export-components */
import { type FC, type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'lawyer' | 'secretary';
  authVersion: number;
};

type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<AuthUser>;
  refreshAuth: () => Promise<AuthUser | null>;
};

type AuthApiResponse = {
  success: boolean;
  authenticated: boolean;
  user?: AuthUser | null;
  error?: string;
};

const AuthContext = createContext<AuthState | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshAuth = useCallback(async () => {
    setLoading(true);

    try {
      const response = await fetch('/api/auth/me', {
        credentials: 'same-origin',
      });
      const data = (await response.json()) as AuthApiResponse;

      if (!response.ok || !data.authenticated || !data.user) {
        setUser(null);
        return null;
      }

      setUser(data.user);
      return data.user;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshAuth();
  }, [refreshAuth]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ email, password }),
    });
    const data = (await response.json()) as AuthApiResponse;

    if (!response.ok || !data.authenticated || !data.user) {
      throw new Error(data.error || 'Não foi possível autenticar.');
    }

    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'same-origin',
      });
    } finally {
      setUser(null);
    }
  }, []);
  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      const data = (await response.json()) as AuthApiResponse;

      if (!response.ok || !data.authenticated || !data.user) {
        throw new Error(data.error || 'Não foi possível alterar a senha.');
      }

      setUser(data.user);
      return data.user;
    },
    []
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      login,
      logout,
      changePassword,
      refreshAuth,
    }),
    [changePassword, loading, login, logout, refreshAuth, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
